import React, { useState } from 'react';
import { Button, IconButton, TextField, CircularProgress, Dialog, DialogContent, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';

const ChatBot = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const openModal = () => setModalIsOpen(true);

  const closeModal = () => {
    setModalIsOpen(false);
    setInputValue('');
    setImage(null);
    setImagePreview(null);
    setIsLoading(false);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && !image) {
      alert('Please add a message or upload an image');
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append('customer_message', inputValue.trim());
    formData.append('email', email.trim());

    if (image) {
      formData.append('image', image);
    }

    try {
      await axios.post('https://joshmachapi.onrender.com/api/prescriptions/add', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert('Prescription uploaded successfully! We will get back to you.');
      closeModal();
    } catch (error) {
      console.error('Error sending prescription:', error);
      alert('Failed to upload. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  return (
    <>
      <Button
        onClick={openModal}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 20,
          minWidth: 56,
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: '#4caf50',
          color: '#fff',
          fontSize: 24,
          boxShadow: '0 4px 16px rgba(76,175,80,0.4)',
          zIndex: 999,
          '&:hover': { backgroundColor: '#388e3c', transform: 'scale(1.1)' },
          transition: 'all 0.2s',
        }}
      >
        📋
      </Button>

      <Dialog
        open={modalIsOpen}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            mx: 2,
            maxHeight: '85vh',
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
            Upload Prescription
          </Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Upload a photo of your prescription and we'll prepare your medication.
          </Typography>

          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            variant="outlined"
            label="Describe your needs (optional)"
            fullWidth
            multiline
            rows={3}
            disabled={isLoading}
            sx={{ mb: 2 }}
          />

          <TextField
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
            label="Your Email"
            fullWidth
            disabled={isLoading}
            sx={{ mb: 2 }}
          />

          {/* File Upload Area */}
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              backgroundColor: imagePreview ? '#f0f9f0' : '#fafafa',
              transition: 'all 0.2s',
              '&:hover': { borderColor: '#4caf50', backgroundColor: '#f0f9f0' },
            }}
            onClick={() => document.getElementById('prescription-file-input').click()}
          >
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
              />
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#aaa', mb: 1 }} />
                <Typography variant="body2" sx={{ color: '#888' }}>
                  Tap to upload prescription image
                </Typography>
              </>
            )}
          </Box>
          <input
            id="prescription-file-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={isLoading}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleSendMessage}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#4caf50',
              fontWeight: 600,
              fontSize: 15,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#388e3c' },
            }}
          >
            {isLoading ? 'Uploading...' : 'Submit Prescription'}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatBot;
